// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import {
    DEFAULT_TAX_TYPE,
    TAX_ID_KEYS,
} from './franceConstants';
import {
    AddressForm,
    AddressInfoPayload,
    AmountPayload,
    ContactInfoPayload,
    DocumentInfoPayload,
    LineItemPayload,
    ManualInvoiceFormState,
    TaxDetailsPayload,
} from './types';

// Rounds a number to 2 decimal places (cents). The + Number.EPSILON term only nudges values
// near the representable boundary (< ~1); for the larger amounts this form handles it is absorbed
// before Math.round, so this is effectively Math.round(value * 100) / 100 — it is NOT general
// floating-point-drift protection.
export function round2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

// Returns true when the string parses to a finite number (tolerating comma decimal separators).
// Blank input is NOT numeric. Callers use this to reject non-numeric amounts before parseAmount
// coerces them to 0.
export function isNumericAmount(value: string): boolean {
    if (value === undefined || value === null) {
        return false;
    }
    const normalized = String(value).trim().replace(',', '.');
    if (normalized === '') {
        return false;
    }
    return Number.isFinite(Number(normalized));
}

// Parses a decimal string, tolerating comma decimal separators. Returns 0 for
// blank/invalid input (callers validate separately before building the payload).
export function parseAmount(value: string): number {
    if (value === undefined || value === null) {
        return 0;
    }
    const normalized = String(value).trim().replace(',', '.');
    if (normalized === '') {
        return 0;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
}

function amount(value: number): AmountPayload {
    return { totalAmount: round2(value) };
}

// Converts an HTML date input value (YYYY-MM-DD) to a date-only ISO string
// WITHOUT a timezone suffix, so the backend parses the exact calendar date the
// user entered (no UTC shift). Empty input yields "".
export function toDateOnlyIso(dateInput: string): string {
    if (!dateInput) {
        return '';
    }
    return `${dateInput}T00:00:00`;
}

// Adds an optional string property only when it has a non-empty value.
function setOptionalString(target: Record<string, unknown>, key: string, value: string): void {
    const trimmed = (value ?? '').trim();
    if (trimmed !== '') {
        target[key] = trimmed;
    }
}

// customerId is Required.Always on the DocumentInfo contract and its value passes through
// TreatEmptyValueAsNullStringConverter, which REJECTS a null/empty/whitespace value at
// deserialization time — so an empty customerId fails both this dry-run validator and real
// ingestion. The Edicom France transformation does not use customerId, and the Manual Invoice
// Creator no longer collects it, so synthesize a stable, non-empty identifier from the buyer
// (the customer): prefer the buyer's tax registration numbers, then company name, and fall back to
// a constant so the payload always deserializes.
const CUSTOMER_ID_FALLBACK = 'MANUAL-INVOICE';

export function deriveCustomerId(form: ManualInvoiceFormState): string {
    const candidates = [
        form.customerId,
        form.buyer.siren,
        form.buyer.vatId,
        form.buyer.siret,
        form.buyer.companyName,
    ];
    for (const candidate of candidates) {
        const trimmed = (candidate ?? '').trim();
        if (trimmed !== '') {
            return trimmed;
        }
    }
    return CUSTOMER_ID_FALLBACK;
}

function buildAddress(addr: AddressForm): AddressInfoPayload {
    // addressType/countryCode are required on AddressInfo; the seller address type
    // is emitted by callers (seller uses billTo semantics on DocumentInfo.SellerAddress).
    const payload: AddressInfoPayload = {
        addressType: 'billTo',
        countryCode: (addr.countryCode || '').trim(),
    } as AddressInfoPayload;
    setOptionalString(payload as unknown as Record<string, unknown>, 'line1', addr.line1);
    setOptionalString(payload as unknown as Record<string, unknown>, 'line2', addr.line2);
    setOptionalString(payload as unknown as Record<string, unknown>, 'line3', addr.line3);
    setOptionalString(payload as unknown as Record<string, unknown>, 'city', addr.city);
    setOptionalString(payload as unknown as Record<string, unknown>, 'postalCode', addr.postalCode);
    return payload;
}

function buildContact(companyName: string, firstName: string, lastName: string, email: string, phone: string): ContactInfoPayload {
    const contact: ContactInfoPayload = {};
    setOptionalString(contact as Record<string, unknown>, 'companyName', companyName);
    setOptionalString(contact as Record<string, unknown>, 'firstName', firstName);
    setOptionalString(contact as Record<string, unknown>, 'lastName', lastName);
    setOptionalString(contact as Record<string, unknown>, 'email', email);
    setOptionalString(contact as Record<string, unknown>, 'phoneNumber', phone);
    return contact;
}

function buildTaxIds(vatId: string, siren: string, siret: string): Record<string, string> | undefined {
    const dict: Record<string, string> = {};
    if (vatId && vatId.trim() !== '') {
        dict[TAX_ID_KEYS.VATID] = vatId.trim();
    }
    if (siren && siren.trim() !== '') {
        dict[TAX_ID_KEYS.SIREN] = siren.trim();
    }
    if (siret && siret.trim() !== '') {
        dict[TAX_ID_KEYS.SIRET] = siret.trim();
    }
    return Object.keys(dict).length > 0 ? dict : undefined;
}

interface ComputedLine {
    payload: LineItemPayload;
    vatRate: number | '';
    preTax: number;
    tax: number;
}

// Computes a single line item's amounts from quantity, unit price and VAT rate,
// guaranteeing preTax = qty*unitPrice, tax = round2(preTax*rate/100), total = preTax+tax.
function computeLine(index: number, form: ManualInvoiceFormState): ComputedLine {
    const line = form.lineItems[index];
    const qty = parseAmount(line.quantity);
    const unit = parseAmount(line.unitPrice);
    const preTax = round2(qty * unit);
    const rate = line.vatRate === '' ? 0 : line.vatRate;
    const tax = round2((preTax * rate) / 100);
    const total = round2(preTax + tax);

    const payload: LineItemPayload = {
        lineItemId: (line.lineItemId || '').trim() || String(index + 1),
        quantity: qty,
        unitPrice: amount(unit),
        preTaxAmount: amount(preTax),
        taxableAmount: amount(preTax),
        taxAmount: amount(tax),
        totalAmount: amount(total),
    };
    setOptionalString(payload as unknown as Record<string, unknown>, 'lineItemDescription', line.description);

    if (line.vatRate !== '') {
        const taxDetail: TaxDetailsPayload = {
            preTaxAmount: amount(preTax),
            nonTaxableAmount: amount(0),
            taxableAmount: amount(preTax),
            taxAmount: amount(tax),
            taxPercentage: line.vatRate,
            taxType: DEFAULT_TAX_TYPE,
        };
        payload.taxDetails = [taxDetail];
    }

    return { payload, vatRate: line.vatRate, preTax, tax };
}

// Groups computed lines by VAT rate to build document-level tax breakdown entries.
function buildDocumentTaxDetails(lines: ComputedLine[]): TaxDetailsPayload[] {
    const byRate = new Map<number, { preTax: number; tax: number }>();
    for (const line of lines) {
        if (line.vatRate === '') {
            continue;
        }
        const bucket = byRate.get(line.vatRate) ?? { preTax: 0, tax: 0 };
        bucket.preTax = round2(bucket.preTax + line.preTax);
        bucket.tax = round2(bucket.tax + line.tax);
        byRate.set(line.vatRate, bucket);
    }

    const details: TaxDetailsPayload[] = [];
    for (const [rate, bucket] of Array.from(byRate.entries()).sort((a, b) => a[0] - b[0])) {
        details.push({
            preTaxAmount: amount(bucket.preTax),
            nonTaxableAmount: amount(0),
            taxableAmount: amount(bucket.preTax),
            taxAmount: amount(bucket.tax),
            taxPercentage: rate,
            taxType: DEFAULT_TAX_TYPE,
        });
    }
    return details;
}

/**
 * Builds the DocumentInfo payload from the form state. Amounts for invoice/credit
 * note are computed from line items; PaymentReceipt uses the manually entered
 * document amounts. Required contract fields are always emitted (including zeros
 * and the possibly-empty lineItems array); optional fields are emitted only when
 * populated.
 */
export function buildPayload(form: ManualInvoiceFormState): DocumentInfoPayload {
    const isPaymentReceipt = form.documentType === 'paymentReceipt';

    const computedLines = isPaymentReceipt
        ? []
        : form.lineItems.map((_, i) => computeLine(i, form));

    let preTaxTotal: number;
    let taxTotal: number;
    let grandTotal: number;
    let taxableTotal: number;
    let docTaxDetails: TaxDetailsPayload[];

    if (isPaymentReceipt) {
        preTaxTotal = round2(parseAmount(form.manualAmounts.preTaxAmount));
        taxableTotal = round2(parseAmount(form.manualAmounts.taxableAmount));
        taxTotal = round2(parseAmount(form.manualAmounts.taxAmount));
        grandTotal = round2(parseAmount(form.manualAmounts.totalAmount));
        // A non-zero payment-receipt tax must still carry a VAT breakdown so the
        // document tax total reconciles with taxDetails. Derive the single-bucket
        // rate from the manually entered taxable base.
        docTaxDetails = taxTotal !== 0 && taxableTotal > 0
            ? [{
                preTaxAmount: amount(taxableTotal),
                nonTaxableAmount: amount(0),
                taxableAmount: amount(taxableTotal),
                taxAmount: amount(taxTotal),
                taxPercentage: round2((taxTotal / taxableTotal) * 100),
                taxType: DEFAULT_TAX_TYPE,
            }]
            : [];
    } else {
        preTaxTotal = round2(computedLines.reduce((sum, l) => sum + l.preTax, 0));
        taxTotal = round2(computedLines.reduce((sum, l) => sum + l.tax, 0));
        taxableTotal = preTaxTotal;
        grandTotal = round2(preTaxTotal + taxTotal);
        docTaxDetails = buildDocumentTaxDetails(computedLines);
    }

    const payload: DocumentInfoPayload = {
        id: form.documentId.trim(),
        documentType: form.documentType,
        countryCode: form.countryCode.trim(),
        documentDate: toDateOnlyIso(form.documentDate),
        billingStartDate: toDateOnlyIso(form.billingStartDate || form.documentDate),
        billingEndDate: toDateOnlyIso(form.billingEndDate || form.documentDate),
        taxCalculationDate: toDateOnlyIso(form.taxCalculationDate || form.documentDate),
        transactionCurrencyCode: form.transactionCurrencyCode.trim(),
        companyCode: form.companyCode.trim(),
        sellerContactInfo: buildContact(
            form.seller.companyName,
            form.seller.firstName,
            form.seller.lastName,
            form.seller.email,
            form.seller.phoneNumber
        ),
        sellerAddress: buildAddress(form.seller.address),
        customerId: deriveCustomerId(form),
        customerAddresses: [
            {
                ...buildAddress(form.buyer.address),
                addressType: form.buyer.addressType,
            },
        ],
        preTaxAmount: amount(preTaxTotal),
        taxableAmount: amount(taxableTotal),
        taxAmount: amount(taxTotal),
        totalAmount: amount(grandTotal),
        lineItems: computedLines.map((l) => l.payload),
        documentCreatedDateInUtc: new Date().toISOString(),
    };

    setOptionalString(payload as unknown as Record<string, unknown>, 'supplyType', form.supplyType);
    setOptionalString(payload as unknown as Record<string, unknown>, 'purchaseOrderNumber', form.purchaseOrderNumber);
    setOptionalString(payload as unknown as Record<string, unknown>, 'documentDescription', form.documentDescription);
    if (form.paymentDueDate) {
        payload.paymentDueDate = toDateOnlyIso(form.paymentDueDate);
    }

    const sellerTaxIds = buildTaxIds(form.seller.vatId, form.seller.siren, form.seller.siret);
    if (sellerTaxIds) {
        payload.sellerTaxIds = sellerTaxIds;
    }

    const buyerContact = buildContact(form.buyer.companyName, form.buyer.firstName, '', form.buyer.email, form.buyer.phoneNumber);
    if (Object.keys(buyerContact).length > 0) {
        payload.customerContactInfo = buyerContact;
    }

    const buyerTaxIds = buildTaxIds(form.buyer.vatId, form.buyer.siren, form.buyer.siret);
    if (buyerTaxIds) {
        payload.taxIds = buyerTaxIds;
    }

    if (docTaxDetails.length > 0) {
        payload.taxDetails = docTaxDetails;
    }

    if (form.documentType === 'creditNote' && form.reference.documentId.trim() !== '') {
        payload.referenceDocument = {
            id: form.reference.documentId.trim(),
            date: toDateOnlyIso(form.reference.date || form.documentDate),
        };
    }

    if (form.invoiceTypeCode === 'TD17') {
        payload.additionalProperties = { invoiceTypeCode: 'TD17' };
    }

    return payload;
}

export function serializePayload(form: ManualInvoiceFormState): string {
    return JSON.stringify(buildPayload(form), null, 2);
}
